import { HOURS_PER_YEAR } from "@/lib/constants";
import { dteAtCursor, resolveExpiryForDte } from "@/lib/market/hours";
import type { OptionContract } from "@/types/market";
import type { OptionLeg } from "@/types/options";
import type { StrategyLayer } from "@/types/strategy";

export type EntryPremiumSource =
  | "manual"
  | "market_mid"
  | "last"
  | "model"
  | "missing";

export interface LegExpiryResolution {
  dte: number;
  expiry: string | null;
  role: "single" | "front" | "back";
}

export interface LayerExpirySummaryItem {
  dte: number;
  expiry: string | null;
  role: LegExpiryResolution["role"];
}

export interface EntryPremiumResolution {
  source: EntryPremiumSource;
  value: number | undefined;
}

export const EXPIRY_ANCHOR_MS_PARAM = "expiry_anchor_ms";

export function layerEntryAnchorMs(
  layer: Pick<StrategyLayer, "params">,
  fallbackMs: number,
): number {
  const value = layer.params[EXPIRY_ANCHOR_MS_PARAM];
  return Number.isFinite(value) ? Number(value) : fallbackMs;
}

export function layerExpiryAnchorDate(
  layer: Pick<StrategyLayer, "params">,
  fallback: Date = new Date(),
): Date {
  return new Date(layerEntryAnchorMs(layer, fallback.getTime()));
}

export function resolveLayerExpiryForDte(
  layer: Pick<StrategyLayer, "params">,
  dte: number,
  holidays: Set<string>,
  fallbackAnchor: Date = new Date(),
) {
  return resolveExpiryForDte(
    dte,
    holidays,
    layerExpiryAnchorDate(layer, fallbackAnchor),
  );
}

export function layerEntryTimeToExpiry(
  layer: Pick<StrategyLayer, "params">,
  dte: number,
  holidays: Set<string>,
  fallbackEntryCursorMs: number,
): number {
  const entryCursorMs = layerEntryAnchorMs(layer, fallbackEntryCursorMs);
  return Math.max(
    0,
    (resolveLayerExpiryForDte(layer, dte, holidays, new Date(entryCursorMs))
      .closeMs -
      entryCursorMs) /
      (HOURS_PER_YEAR * 3600_000),
  );
}

export function evolveManualEntryPremium({
  manual,
  modelCurrent,
}: {
  manual: number | null | undefined;
  modelCurrent: number | null | undefined;
}): number | null {
  // A manual premium is the actual entry cost basis, not a calibration offset
  // that should be carried into every future mark. Shifting it by the model's
  // entry-to-current change can drive an ITM option to zero before expiry and
  // overstate the strategy P&L. Use the current theoretical mark whenever it
  // is available; only fall back to the manual entry premium when pricing is
  // unavailable.
  if (Number.isFinite(modelCurrent)) return Math.abs(modelCurrent as number);
  return Number.isFinite(manual) ? Math.abs(manual as number) : null;
}

export function legExpiryDteParamKey(index: number): string {
  return `expiry_leg_${index}`;
}

export function legExpiryDte(
  layer: { kind?: StrategyLayer["kind"]; params: Record<string, number> },
  leg: Pick<OptionLeg, "side">,
  fallbackDte: number,
  legIndex?: number,
): number {
  if (legIndex != null) {
    const legDte = layer.params[legExpiryDteParamKey(legIndex)];
    if (Number.isFinite(legDte)) return Number(legDte);
  }

  const frontDte = layer.params.frontDTE;
  const backDte = layer.params.backDTE;
  const hasTermStructure =
    Number.isFinite(frontDte) && Number.isFinite(backDte);
  if (hasTermStructure && leg.side === "short") return Number(frontDte);
  if (hasTermStructure && leg.side === "long") return Number(backDte);

  return Number.isFinite(layer.params.expiryDTE)
    ? Number(layer.params.expiryDTE)
    : fallbackDte;
}

export function layerExpiryDtes(
  layer: { kind?: StrategyLayer["kind"]; params: Record<string, number> },
  fallbackDte: number,
  includeHiddenLegs = false,
): number[] {
  if (layer.kind === "custom") {
    const visibleDtes = customLayerExpiryDtes(
      layer,
      fallbackDte,
      includeHiddenLegs,
    );
    return [...new Set(visibleDtes.length > 0 ? visibleDtes : [fallbackDte])];
  }

  const frontDte = Number(layer.params.frontDTE);
  const backDte = Number(layer.params.backDTE);
  const hasTermStructure =
    Number.isFinite(frontDte) && Number.isFinite(backDte);
  const values = (
    hasTermStructure ? [frontDte, backDte] : [layer.params.expiryDTE]
  )
    .map(Number)
    .filter((value) => Number.isFinite(value));
  return [...new Set(values.length > 0 ? values : [fallbackDte])];
}

export function legTimeToExpiry(
  layer: { kind?: StrategyLayer["kind"]; params: Record<string, number> },
  leg: Pick<OptionLeg, "side">,
  fallbackT: number,
  _fallbackDte: number,
  legIndex?: number,
): number {
  // Keep entry pricing aligned with the Rust strategy kernel. A regular
  // single-expiry layer receives an exact timestamp-derived `fallbackT`;
  // converting its display DTE back to `dte / 365` loses the intraday
  // fraction and makes per-leg P&L drift away from the strategy total.
  // Only custom per-leg expiries and term structures own a structural T.
  const usesCustomLegExpiries =
    layer.kind === "custom" &&
    Object.keys(layer.params).some((key) => key.startsWith("expiry_leg_"));
  if (usesCustomLegExpiries) {
    const legDte =
      legIndex == null
        ? undefined
        : layer.params[legExpiryDteParamKey(legIndex)];
    if (Number.isFinite(legDte)) return Math.max(0, Number(legDte)) / 365;
    if (Number.isFinite(layer.params.expiryDTE)) {
      return Math.max(0, Number(layer.params.expiryDTE)) / 365;
    }
  }

  const hasTermStructure =
    Number.isFinite(layer.params.frontDTE) &&
    Number.isFinite(layer.params.backDTE);
  if (hasTermStructure) {
    return (
      Math.max(
        0,
        leg.side === "short"
          ? Number(layer.params.frontDTE)
          : Number(layer.params.backDTE),
      ) / 365
    );
  }

  return fallbackT;
}

export function resolveLegExpiry(
  layer: StrategyLayer,
  leg: OptionLeg,
  fallbackDte: number,
  holidays: Set<string>,
  legIndex?: number,
): LegExpiryResolution {
  const frontDte = layer.params.frontDTE;
  const backDte = layer.params.backDTE;
  const hasTermStructure =
    Number.isFinite(frontDte) && Number.isFinite(backDte);
  const role =
    hasTermStructure && leg.side === "short"
      ? "front"
      : hasTermStructure && leg.side === "long"
        ? "back"
        : "single";
  const dte = legExpiryDte(layer, leg, fallbackDte, legIndex);
  const resolved = resolveLayerExpiryForDte(layer, dte, holidays);

  return {
    dte: dteAtCursor(Date.now(), resolved.closeMs),
    expiry: resolved.dateStr,
    role,
  };
}

export function resolveLayerExpirySummary(
  layer: StrategyLayer,
  fallbackDte: number,
  holidays: Set<string>,
): LayerExpirySummaryItem[] {
  if (layer.kind === "custom") {
    const resolved = [...new Set(customLayerExpiryDtes(layer, fallbackDte))]
      .sort((a, b) => a - b)
      .map((dte) => {
        const expiry = resolveLayerExpiryForDte(layer, dte, holidays);
        return {
          dte: dteAtCursor(Date.now(), expiry.closeMs),
          expiry: expiry.dateStr,
          role: "single" as const,
        };
      });
    return resolved.filter(
      (item, index) =>
        resolved.findIndex((candidate) => candidate.expiry === item.expiry) ===
        index,
    );
  }

  const frontDte = layer.params.frontDTE;
  const backDte = layer.params.backDTE;
  if (Number.isFinite(frontDte) && Number.isFinite(backDte)) {
    const front = resolveLayerExpiryForDte(layer, Number(frontDte), holidays);
    const back = resolveLayerExpiryForDte(layer, Number(backDte), holidays);
    return [
      {
        dte: dteAtCursor(Date.now(), front.closeMs),
        expiry: front.dateStr,
        role: "front",
      },
      {
        dte: dteAtCursor(Date.now(), back.closeMs),
        expiry: back.dateStr,
        role: "back",
      },
    ];
  }

  const dte = Number.isFinite(layer.params.expiryDTE)
    ? Number(layer.params.expiryDTE)
    : fallbackDte;
  const resolved = resolveLayerExpiryForDte(layer, dte, holidays);
  return [
    {
      dte: dteAtCursor(Date.now(), resolved.closeMs),
      expiry: resolved.dateStr,
      role: "single",
    },
  ];
}

function customLayerExpiryDtes(
  layer: { params: Record<string, number> },
  fallbackDte: number,
  includeHiddenLegs = false,
): number[] {
  const legCount = Math.max(0, Math.round(layer.params.leg_count ?? 0));
  if (legCount === 0) return [];

  return Array.from({ length: legCount }, (_, index) => {
    if (!includeHiddenLegs && layer.params[`leg_off_${index}`] === 1)
      return null;
    const legDte = layer.params[legExpiryDteParamKey(index)];
    if (Number.isFinite(legDte)) return Number(legDte);
    return Number.isFinite(layer.params.expiryDTE)
      ? Number(layer.params.expiryDTE)
      : fallbackDte;
  }).filter(
    (value): value is number => value != null && Number.isFinite(value),
  );
}

export function resolveEntryPremium({
  manual,
  model,
  quote,
}: {
  manual: number | undefined;
  model: number | undefined;
  quote: OptionContract | null;
}): EntryPremiumResolution {
  if (Number.isFinite(manual)) {
    return { source: "manual", value: Math.abs(manual as number) };
  }
  const mid = quoteMid(quote);
  if (mid != null) return { source: "market_mid", value: mid };
  const last = quote?.last_price;
  if (last != null && Number.isFinite(last) && last > 0) {
    return { source: "last", value: last };
  }
  if (Number.isFinite(model)) {
    return { source: "model", value: model };
  }
  return { source: "missing", value: undefined };
}

export function findLegQuote(
  chain: OptionContract[],
  expiry: string | null,
  leg: Pick<OptionLeg, "strike" | "type">,
): OptionContract | null {
  if (!expiry) return null;
  return (
    chain.find(
      (row) =>
        row.expiry === expiry &&
        row.contract_type === leg.type &&
        row.strike === leg.strike,
    ) ?? null
  );
}

function quoteMid(quote: OptionContract | null): number | null {
  const bid = quote?.bid;
  const ask = quote?.ask;
  if (
    bid != null &&
    ask != null &&
    Number.isFinite(bid) &&
    Number.isFinite(ask) &&
    bid > 0 &&
    ask > 0
  ) {
    return (bid + ask) / 2;
  }
  return null;
}
